/**
 * The manual adjustment on the fee an Armadillo assignment's registry
 * orders set: the automatic assignment's, or that of a manual assignment
 * locked to Armadillo while it is live (the Hub writes both from the
 * orders, so the adjustment is the one lever on either).
 *
 * The order rules cannot always match Armadillo's invoice: a quote order
 * delivered before its quote was recorded stays at £0, an extra charge
 * (courier, apostille) has nowhere to go, a closed or failed order may
 * still be charged. The adjustment is a signed amount added to the orders'
 * total, never below £0, with a reason that stays on the card and in the
 * fee history. On a row that bills no order any more (every one closed or
 * failed) the fee is the adjustment alone, so the same control reads as
 * recording a charge. Admin-only, like the other assignment routes; the
 * API holds the same limits the form checks.
 */

import { useState } from 'react';

import { Button, Group, Modal, NumberInput, Stack, Text, Textarea } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { IconAdjustments } from '@tabler/icons-react';

import {
  billsNoOrders,
  canAdjustRegistryFee,
  formatSignedGbp,
  REGISTRY_FEE_ADJUSTMENT_MAX,
  REGISTRY_FEE_ADJUSTMENT_REASON_MAX,
  type BackofficeAssignment,
  type RegistryFeeAdjustmentRequest,
} from '@/types/np-assignments';
import { formatGbp } from '@/types/registry-documents';
import { useNpAssignmentsContext } from './NpAssignmentsContext';

/** What the form refuses before the API sees it. */
export function adjustmentAmountError(value: number | string): string | null {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return 'Enter an amount';
  }
  if (value === 0) {
    return 'An adjustment of £0 changes nothing';
  }
  if (Math.abs(value) > REGISTRY_FEE_ADJUSTMENT_MAX) {
    return `Keep it within ±${formatGbp(REGISTRY_FEE_ADJUSTMENT_MAX)}`;
  }
  return null;
}

export function adjustmentReasonError(value: string): string | null {
  const typed = value.trim().length;
  if (typed === 0) {
    return 'Say why the fee is adjusted';
  }
  if (typed > REGISTRY_FEE_ADJUSTMENT_REASON_MAX) {
    return `At most ${REGISTRY_FEE_ADJUSTMENT_REASON_MAX} characters`;
  }
  return null;
}

/** "Orders total £80.00 · current adjustment +£15.00 (courier fee)": what
 * the form starts from. */
export function describeAdjustmentStart(
  assignment: Pick<BackofficeAssignment, 'registryFeeTotal' | 'feeAdjustment' | 'feeAdjustmentNote'>
): string {
  const total = `Orders total ${formatGbp(assignment.registryFeeTotal ?? 0)}`;
  if (assignment.feeAdjustment === null) {
    return total;
  }
  const note = assignment.feeAdjustmentNote ? ` (${assignment.feeAdjustmentNote})` : '';
  return `${total} · current adjustment ${formatSignedGbp(assignment.feeAdjustment)}${note}`;
}

/** The words the dialog uses: an adjustment to a fee the orders set, or a
 * charge on a row whose orders bill nothing any more. */
export interface AdjustmentWording {
  action: string;
  title: string;
  intro: string;
  /** The amount field's label. */
  amount: string;
  save: string;
  remove: string;
}

const ADJUST_WORDING: AdjustmentWording = {
  action: 'Adjust fee',
  title: "Adjust Armadillo's fee",
  intro:
    "For what the order rules cannot match: a charge with no order to carry it, an order charged after it closed, a quote that came after delivery. The fee becomes the orders' total plus this amount, never below £0.",
  amount: 'Adjustment',
  save: 'Save adjustment',
  remove: 'Remove adjustment',
};

const CHARGE_WORDING: AdjustmentWording = {
  action: 'Record a charge',
  title: 'Record a charge for Armadillo',
  intro:
    'No order on this assignment is billed: every one closed or failed. Armadillo may still charge for one, a failed trace say. The fee becomes this amount alone, never below £0, and the assignment bills again; removing the charge takes it back to nothing.',
  amount: 'Charge',
  save: 'Record charge',
  remove: 'Remove charge',
};

export function adjustmentWording(
  assignment: Pick<BackofficeAssignment, 'registryOrders'>
): AdjustmentWording {
  return billsNoOrders(assignment) ? CHARGE_WORDING : ADJUST_WORDING;
}

interface AdjustmentFormValues {
  amount: number | string;
  reason: string;
}

interface AdjustmentFormProps {
  assignment: BackofficeAssignment;
  submitting: boolean;
  onSubmit: (request: RegistryFeeAdjustmentRequest) => void;
  onCancel: () => void;
}

/** The fields. Mounted afresh each time the dialog opens, so the amount
 * starts from the adjustment in force and the reason starts blank. */
function AdjustmentForm({
  assignment,
  submitting,
  onSubmit,
  onCancel,
}: Readonly<AdjustmentFormProps>) {
  const form = useForm<AdjustmentFormValues>({
    initialValues: { amount: assignment.feeAdjustment ?? '', reason: '' },
    validate: { amount: adjustmentAmountError, reason: adjustmentReasonError },
  });
  const existing = assignment.feeAdjustment !== null;
  const wording = adjustmentWording(assignment);

  const save = form.onSubmit((values) => {
    onSubmit({ amount: values.amount as number, reason: values.reason.trim() });
  });

  // Clearing needs a reason too; the amount field is left out of it.
  const remove = () => {
    if (form.validateField('reason').hasError) {
      return;
    }
    onSubmit({ amount: null, reason: form.values.reason.trim() });
  };

  return (
    <form onSubmit={save}>
      <Stack gap="sm">
        <Text size="sm" data-testid="registry-fee-adjustment-intro">
          {wording.intro}
        </Text>
        <Text size="xs" c="dimmed" data-testid="registry-fee-adjustment-current">
          {describeAdjustmentStart(assignment)}
        </Text>
        <NumberInput
          label={wording.amount}
          description="In GBP, pence allowed; a negative amount takes from the fee"
          prefix="£"
          decimalScale={2}
          allowNegative
          data-autofocus
          data-testid="registry-fee-adjustment-amount"
          {...form.getInputProps('amount')}
        />
        <Textarea
          label="Reason"
          description="Shown on the card and kept in the fee history"
          placeholder="e.g. Courier fee on the apostilled extract"
          autosize
          minRows={2}
          maxLength={REGISTRY_FEE_ADJUSTMENT_REASON_MAX}
          data-testid="registry-fee-adjustment-reason"
          {...form.getInputProps('reason')}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          {existing && (
            <Button
              variant="subtle"
              color="red"
              onClick={remove}
              disabled={submitting}
              data-testid="registry-fee-adjustment-remove"
            >
              {wording.remove}
            </Button>
          )}
          <Button type="submit" loading={submitting} data-testid="registry-fee-adjustment-confirm">
            {wording.save}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

interface RegistryFeeAdjustmentDialogProps {
  opened: boolean;
  onClose: () => void;
  assignment: BackofficeAssignment;
  /** Resolves to whether the adjustment took. A refusal has already been
   * shown by then, so the dialog stays open for another go. */
  onConfirm: (request: RegistryFeeAdjustmentRequest) => Promise<boolean>;
}

/** The dialog itself, with the save handed in: what the tests render. */
export function RegistryFeeAdjustmentDialog({
  opened,
  onClose,
  assignment,
  onConfirm,
}: Readonly<RegistryFeeAdjustmentDialogProps>) {
  const [submitting, setSubmitting] = useState(false);

  const submit = async (request: RegistryFeeAdjustmentRequest) => {
    setSubmitting(true);
    try {
      if (await onConfirm(request)) {
        onClose();
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={adjustmentWording(assignment).title}
      // A save in flight is not abandoned by dismissing the dialog: its
      // outcome must still have somewhere to be shown.
      closeOnClickOutside={!submitting}
      closeOnEscape={!submitting}
      closeButtonProps={{ disabled: submitting }}
      data-testid="registry-fee-adjustment-dialog"
    >
      <AdjustmentForm
        assignment={assignment}
        submitting={submitting}
        onSubmit={(request) => void submit(request)}
        onCancel={onClose}
      />
    </Modal>
  );
}

/**
 * "Adjust fee" on an Armadillo assignment, with its dialog: the automatic
 * assignment's card, or beside the read-only fee of a manual assignment
 * locked to Armadillo; "Record a charge" once the orders bill nothing.
 * Nothing on a row a manual assignment took over, nor on any other
 * assignment, whose fee is typed in its own form.
 */
export function RegistryFeeAdjustmentControl({
  assignment,
}: Readonly<{ assignment: BackofficeAssignment }>) {
  const { actions, refetchCommercialChanges } = useNpAssignmentsContext();
  const [opened, { open, close }] = useDisclosure(false);

  if (!canAdjustRegistryFee(assignment)) {
    return null;
  }

  // The API records the adjustment and the recomputed fee in the commercial
  // history, so that is re-read with the cards.
  const confirm = async (request: RegistryFeeAdjustmentRequest) => {
    const adjusted = await actions.setRegistryFeeAdjustment(assignment.id, request);
    if (adjusted) {
      void refetchCommercialChanges();
    }
    return adjusted !== null;
  };

  return (
    <>
      <Button
        size="compact-xs"
        variant="light"
        leftSection={<IconAdjustments size={12} />}
        onClick={open}
        data-testid={`registry-fee-adjust-${assignment.id}`}
      >
        {adjustmentWording(assignment).action}
      </Button>
      <RegistryFeeAdjustmentDialog
        opened={opened}
        onClose={close}
        assignment={assignment}
        onConfirm={confirm}
      />
    </>
  );
}
